import { Dispatch } from '@reduxjs/toolkit';
import globalSlice from './globalSlice';
import { notification } from 'antd';
import {
  IChangePass,
  IFilters,
  IFiltersLei,
  IResetPass,
  ThemeList,
} from '../globalTypes';
import { ITableFilters } from './types';
import ApiService from 'utils/ApiCalls';
import Helpers from 'utils/Helpers';
import axios from 'axios';

const {
  set_theme,
  set_loading,
  set_applied_filters_result,
  set_table_filters,
  set_filters_data,
  set_filters_data_lei,
} = globalSlice.actions;

const setTheme = (theme: ThemeList) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_theme(theme));
  } catch (error) {
    console.log(error);
  }
};

const setAppliedFilersResult = (data: any[]) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_applied_filters_result(data));
  } catch (error) {
    console.log(error);
  }
};

const setTableFilter = (filter: ITableFilters) => (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    dispatch(set_table_filters(filter));
  } catch (error) {
    console.log(error);
  }
};

const setFilters = () => async (dispatch: Dispatch) => {
  try {
    dispatch(set_loading());
    const response = await ApiService.getFiltersByEntity();
    let fetchedFilters: IFilters[] = [];

    if (response.data && !Helpers.isObjEmpty(response.data)) {
      fetchedFilters = Object.values(
        response.data as { id: number; name_of_entity: string }[],
      ).map((entity) => {
        return {
          filter: entity.name_of_entity,
          options: [
            {
              id: entity.id,
              name_of_entity: entity.name_of_entity,
              label: entity.name_of_entity,
              value: entity.id.toString(),
            },
          ],
        };
      });
    }

    dispatch(set_filters_data(fetchedFilters));
  } catch (error) {
    console.log('Error fetching the Entities:', error);
  }
};

const login = async (email: string, password: string) => {
  try {
    const response = await ApiService.login({
      email,
      password,
    });
    return response;
  } catch (error) {
    console.log('Login failed:', error);
    throw error;
  }
};

const superAdminLogin = async (email: string, password: string) => {
  try {
    const response = await ApiService.superAdminLogin({
      email,
      password,
    });
    return response;
  } catch (error) {
    console.log('Super admin login failed:', error);
    throw error;
  }
};

const verifyLogin = async (token: string) => {
  try {
    const response = await ApiService.verifyLogin(token);
    return response;
  } catch (error) {
    console.log('Login failed:', error);
    throw error;
  }
};

const verifySuperAdminLogin = async (token: string) => {
  try {
    const response = await ApiService.verifySuperAdminLogin(token);
    return response;
  } catch (error) {
    console.log('Login failed:', error);
    throw error;
  }
};

const changePass = async (
  formData: IChangePass,
  linkToken: string | qs.ParsedQs | string[] | qs.ParsedQs[],
) => {
  try {
    const response = await ApiService.changePass(formData, linkToken);

    notification.success({
      message: 'Info',
      description: 'Your password has been updated.',
      duration: 20,
    });
  } catch (error) {
    console.log(error);
  }
};

const logout = async () => {
  try {
    const response = await ApiService.logout();
    return response.data;
  } catch (error) {
    console.log('Logout failed:', error);
    throw error;
  }
};

const signup = async (data: any) => {
  try {
    const response = await ApiService.signup(data);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'An unexpected error occurred';

    notification.error({
      message: 'Signup Failed',
      description: errorMessage,
      duration: 10,
    });

    throw new Error(errorMessage);
  }
};

const forgotPassword = async (email: string) => {
  try {
    const response = await ApiService.forgotPassword(email);
    return response.data;
  } catch (error) {
    console.log('Forgot password request failed:', error);
    throw error;
  }
};

const resetPassword = async (formData: IResetPass) => {
  try {
    const response = await ApiService.resetPassword(formData);

    notification.success({
      message: 'Info',
      description: 'Your password has been reseted successfully.',
      duration: 20,
    });
  } catch (error) {
    console.log(error);
  }
};

const userActivityStatus = async (username: string) => {
  try {
    const response = await ApiService.fetchUserActivityStatus(username);
    return response.data;
  } catch (error) {
    console.log('Activity status request failed:', error);
    throw error;
  }
};

const fetchSensitivenessOfData = async (entity_id: any) => {
  try {
    const response = await ApiService.getSensitivenessOfData(entity_id);
    return response;
  } catch (error) {
    console.log('Error fetching the sensitiveness of data', error);
  }
};

const fetchSubstitutability = async (entity_id: any) => {
  try {
    const response = await ApiService.getICTServiceSubstitutability(entity_id);
    return response;
  } catch (error) {
    console.log('Error fetching the substitutability', error);
  }
};

const fetchICTSystemsbyEntityName = async (entityTypeData: any) => {
  try {
    const response = await ApiService.postICTSystemsByEntity(entityTypeData);
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.log(
        'Error fetching ICt Sytsems:',
        error.response?.data || error.message,
      );
    } else {
      console.log('An unexpected error occurred:', error);
    }
    throw error;
  }
};

const getActiveUsers = async () => {
  try {
    const response = await ApiService.getActiveUsers();
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

const getUserFunctions = async (user_id: any) => {
  try {
    const response = await ApiService.getUserFunctions(user_id);
    return response;
  } catch (error) {
    console.log(error);
  }
};

const assignUserFunctions = async (user_id: number, function_ids: any) => {
  try {
    const response = await ApiService.assignUserFunctions(
      user_id,
      function_ids,
    );
    notification.success({
      message: 'Info',
      description: response?.data.message,
    });
    return response;
  } catch (error: any) {
    console.log(error);
    notification.error({
      message: 'Info',
      description:
        error?.response?.data?.message || 'Failed to assign functions',
    });
  }
};

const removeUserFunctions = async (user_id: number, function_ids: any) => {
  try {
    const response = await ApiService.removeUserFunctions(
      user_id,
      function_ids,
    );
    notification.success({
      message: 'Info',
      description: response?.data.message,
    });
    return response;
  } catch (error: any) {
    console.log(error);
    notification.error({
      message: 'Error',
      description:
        error?.response?.data?.message || 'Failed to remove functions',
    });
  }
};

const fetchReportDetails = async (table_name: any, entity_id: any) => {
  try {
    const response = await ApiService.getReportDetails(table_name, entity_id);
    return response;
  } catch (error) {
    console.log(error);
  }
};

const updateReportDetails = async (table_name: any, records: any) => {
  try {
    const response = await ApiService.updateReportDetails(table_name, records);
    notification.success({
      message: 'Info',
      description: response?.data.message,
    });
    return response;
  } catch (error: any) {
    console.log(error);
    notification.error({
      message: 'Info',
      description: error?.message,
    });
  }
};

const getUserFunctionRoles = async (active: boolean) => {
  try {
    const response = await ApiService.getUserFunctionRoles(active);
    return response;
  } catch (error: any) {
    notification.error({
      message: 'Info',
      description: error?.message,
    });
  }
};

const getActiveInactiveOptions = async () => {
  try {
    const response = await ApiService.getActiveInactiveOptions();
    return response;
  } catch (error: any) {
    notification.error({
      message: 'Info',
      description: error?.message,
    });
  }
};

const fetchGlobalUsers = async () => {
  try {
    const response = await ApiService.fetchGlobalUsers();
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const signupSuperUser = async (data: any) => {
  try {
    const response = await ApiService.signupSuperUser(data);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'An unexpected error occurred';

    notification.error({
      message: 'Signup Failed',
      description: errorMessage,
      duration: 10,
    });

    throw new Error(errorMessage);
  }
};

const softDeleteUserBySuperAdmin = async (email: any, schema_id: any) => {
  try {
    const response = await ApiService.softDeleteUser(email, schema_id);
    notification.success({
      message: 'Info',
      description: response?.data.message,
    });
    return response;
  } catch (error: any) {
    notification.error({
      message: 'Info',
      description: error?.message,
    });
  }
};

const postGlobalCompanies = async (data: any) => {
  try {
    const response = await ApiService.postGlobalCompanies(data);
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'An unexpected error occurred';

    notification.error({
      message: 'Submission Failed',
      description: errorMessage,
      duration: 10,
    });

    throw new Error(errorMessage);
  }
};

const fetchGlobalCompanies = async () => {
  try {
    const response = await ApiService.fetchGlobalCompanies();
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const fetchGlobalCompanyID = async (
  global_company_id: number,
  schema_id: string,
) => {
  try {
    const response = await ApiService.fetchGlobalCompanyID(
      global_company_id,
      schema_id,
    );
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const createClientSchema = async (
  schemaId: string,
  file: File,
): Promise<string> => {
  try {
    const { data } = await ApiService.createClientSchema(schemaId, file);
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.',
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'SQL Upload Failed',
      description: err,
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed',
    );
  }
};

const uploadExcelEntityTypeActivity = async (
  schemaId: string,
  file: File,
): Promise<string> => {
  try {
    const { data } = await ApiService.uploadExcelEntityTypeActivity(
      schemaId,
      file,
    );
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.',
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'Excel Upload Failed',
      description: err,
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed',
    );
  }
};

const uploadExcelAccessControl = async (
  schemaId: string,
  file: File,
): Promise<string> => {
  try {
    const { data } = await ApiService.uploadExcelAccessControl(schemaId, file);
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.',
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'Excxel Upload Failed',
      description: err,
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed',
    );
  }
};

const uploadParentData = async (
  schemaId: string,
  file: File,
): Promise<string> => {
  try {
    const { data } = await ApiService.uploadParentData(schemaId, file);
    notification.success({
      message: 'Info',
      description: data.message || 'Schema created successfully.',
    });
    return data.message;
  } catch (err: any) {
    notification.error({
      message: 'File Upload Failed',
      description: err,
    });

    throw new Error(
      err?.response?.data?.message || err.message || 'Upload failed',
    );
  }
};

const validateLEI = async (leis: string[]) => {
  try {
    const response = await ApiService.validateLEI(leis);
    return response.data;
  } catch (error) {
    console.log(error);
  }
};

const fetchICTSystemTypes = async () => {
  try {
    const response = await ApiService.getICTSystemTypes();
    return response;
  } catch (error) {
    console.log('Error fetching the ict system types:', error);
  }
};

const fetchICTSystemCategories = async () => {
  try {
    const response = await ApiService.getICTSystemCategories();
    return response;
  } catch (error) {
    console.log('Error fetching the ict system categories:', error);
  }
};

export const setFiltersBylei =
  (financial_entity_lei_id?: number) => async (dispatch: Dispatch) => {
    try {
      dispatch(set_loading());

      const response = await ApiService.getFilters(financial_entity_lei_id);

      let fetchedFilters: IFiltersLei[] = [];

      if (response.data && !Helpers.isObjEmpty(response.data)) {
        fetchedFilters = Object.values(
          response.data as {
            id: number;
            financial_entity_lei_id: number;
            name_of_entity: string;
          }[],
        ).map((entity) => ({
          filter: entity.name_of_entity,
          options: [
            {
              id: entity.id,
              financial_entity_lei_id: entity.financial_entity_lei_id,
              name_of_entity: entity.name_of_entity,
              value: entity.id.toString(),
              label: entity.name_of_entity,
            },
          ],
        }));
      }
      dispatch(set_filters_data_lei(fetchedFilters));
    } catch (error) {
      console.log('Error fetching the Entities:', error);
    }
  };

export default {
  setTheme,
  setAppliedFilersResult,
  setTableFilter,
  setFilters,
  login,
  changePass,
  logout,
  signup,
  forgotPassword,
  resetPassword,
  userActivityStatus,
  fetchSensitivenessOfData,
  fetchSubstitutability,
  fetchICTSystemsbyEntityName,
  getActiveUsers,
  getUserFunctions,
  assignUserFunctions,
  removeUserFunctions,
  fetchReportDetails,
  updateReportDetails,
  verifyLogin,
  getUserFunctionRoles,
  getActiveInactiveOptions,
  superAdminLogin,
  verifySuperAdminLogin,
  fetchGlobalUsers,
  signupSuperUser,
  softDeleteUserBySuperAdmin,
  postGlobalCompanies,
  fetchGlobalCompanies,
  fetchGlobalCompanyID,
  createClientSchema,
  uploadExcelEntityTypeActivity,
  uploadExcelAccessControl,
  uploadParentData,
  validateLEI,
  fetchICTSystemTypes,
  fetchICTSystemCategories,
  setFiltersBylei,
};
